import { Amount, PercentAmount } from '@akropolis-web/primitives';
import React from 'react';
import { LinearProgress, linearProgressClasses } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import { colors, styled } from 'core/styles';
import { attachStaticFields } from 'utils/object';

import { FormattedAmount } from '../FormattedAmount';

type ProgressBarPosition = 'start' | 'end';

type ProgressBarProps = {
  width: number;
  height: number;
  borderRadius: number;
  backgroundColor: string;
  backgroundOpacity: number;
};

type Props<A extends Amount> = {
  value: A;
  total: A;
  renderProgressLabel?: (progress: PercentAmount) => React.ReactNode;
  getColor?: (progress: PercentAmount, theme: Theme) => any;
  progressBarProps?: ProgressBarProps;
  progressBarPosition?: ProgressBarPosition;
};

export const Progress = attachStaticFields(ProgressComponent, {
  getSignColor: (progress: PercentAmount, theme: Theme) => {
    if (progress.isZero()) {
      return theme.palette.progress.default;
    }
    if (progress.lt(0)) {
      return theme.palette.progress.negative;
    }
    return theme.palette.progress.positive;
  },
  getDefaultColor: (_progress: PercentAmount, theme: Theme) => {
    return theme.palette.progress.default;
  },
});

function ProgressComponent<A extends Amount>({
  total,
  value,
  renderProgressLabel = renderDefaultProgressLabel,
  getColor = Progress.getDefaultColor,
  progressBarProps,
  progressBarPosition = 'start',
}: Props<A>) {
  const theme = useTheme();

  const progressValue = new PercentAmount(
    total.isZero() ? 0 : value.div(total).mul(100).toNumber(),
  );

  const isProgressNegative = progressValue.lt(0);

  const progressComponent = (
    <LinearProgressComponent
      props={progressBarProps || null}
      progressValue={progressValue.abs().toNumber()}
      variant="determinate"
      value={progressValue.abs().toNumber()}
      color="inherit"
      progressColor={getColor(progressValue, theme)}
      isProgressNegative={isProgressNegative}
    />
  );

  return (
    <ProgressContainer>
      {progressBarPosition === 'start' && progressComponent}
      {renderProgressLabel(progressValue)}
      {progressBarPosition === 'end' && progressComponent}
    </ProgressContainer>
  );
}

function renderDefaultProgressLabel(progressValue: PercentAmount) {
  return (
    <ProgressValue>
      <FormattedAmount
        sum={progressValue}
        precision={0}
        symbolSize="inherit"
        tooltip={progressValue.toFormattedString(2)}
      />
    </ProgressValue>
  );
}

const ProgressContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
  gap: 12,
}));

const ProgressValue = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: 'stacked-content',
  fontSize: 13,
  color: colors.black,

  [theme.breakpoints.up('tabletLG')]: {
    fontSize: 12,
  },

  [theme.breakpoints.up('desktopXL')]: {
    fontSize: 15,
  },

  '& > *': {
    gridArea: 'stacked-content',
  },

  // place reservation
  '&::after': {
    content: '"99%"',
    visibility: 'hidden',
    gridArea: 'stacked-content',
  },
}));

const LinearProgressComponent = styled(LinearProgress, {
  shouldForwardProp: prop =>
    ![
      'props',
      'progressValue',
      'rating',
      'isProgressNegative',
      'progressColor',
      'progressBarPosition',
    ].includes(String(prop)),
})<{
  props: ProgressBarProps | null;
  progressValue: number;
  isProgressNegative: boolean;
  progressColor: string;
}>(({ props, isProgressNegative, progressValue, progressColor }) => ({
  display: 'inline-block',
  width: props ? props.width : 40,
  height: props ? props.height : 6,
  borderRadius: props ? props.borderRadius : 3,
  color: progressColor,

  '&::before': {
    backgroundColor: props ? props.backgroundColor : progressColor,
    opacity: props ? props.backgroundOpacity : 0.3,
  },

  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: progressColor,
    borderRadius: props ? props.borderRadius : 3,
    transform: isProgressNegative
      ? `translateX(${100 - progressValue}%) !important`
      : `translateX(-${100 - progressValue}%) !important`,
  },
}));
